/* eslint-disable camelcase */
/* eslint-disable prefer-destructuring */
/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Grid,
  Chip,
  Paper,
  Drawer,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';
import Tooltip from '@mui/material/Tooltip';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import * as moment from 'moment';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PhoneIcon from '@mui/icons-material/Phone';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TimelineIcon from '@mui/icons-material/Timeline';
import CommentIcon from '@mui/icons-material/Comment';
import EmailIcon from '@mui/icons-material/Email';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import SchoolIcon from '@mui/icons-material/School';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { motion } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import toast from 'react-hot-toast';
import BusinessIcon from '@material-ui/icons/Business';
import { etablissementApi } from '../../../api/etablissementApi';
import CustomerCommercialActions from './CustomerCommercialActions';
import CustomerRelance from './CustomerRelance';
import CustomerRelationScore from './CustomerRelationScore';

const CommentBubble = ({ comment, index }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.05 }}
    style={{ marginBottom: '1.5rem', position: 'relative' }}
  >
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.light', color: 'white', fontWeight: 800 }}>
        {comment.firstname?.[0] || 'U'}
      </Avatar>
      
      <Paper sx={{ 
        p: 2, 
        flexGrow: 1, 
        borderRadius: '16px', 
        bgcolor: '#f8fafc', 
        border: '1px solid #e2e8f0',
        boxShadow: 'none'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b' }}>
            {comment.firstname} {comment.lastname}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {moment(comment.createdAt).format('DD/MM/YY à HH:mm')}
          </Typography>
        </Box>
        
        <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.5 }}>
          {comment.comment}
        </Typography>
        
        {/* Numbered Formations List */}
        {(comment.nameFormation1 || comment.nameFormation2 || comment.nameFormation3) && (
          <Box sx={{ mt: 1, mb: 1 }}>
            {comment.nameFormation1 && (
              <Typography variant="caption" display="block" sx={{ color: '#475569', fontWeight: 600 }}>
                1) {comment.nameFormation1}
              </Typography>
            )}
            {comment.nameFormation2 && (
              <Typography variant="caption" display="block" sx={{ color: '#475569', fontWeight: 600 }}>
                2) {comment.nameFormation2}
              </Typography>
            )}
            {comment.nameFormation3 && (
              <Typography variant="caption" display="block" sx={{ color: '#475569', fontWeight: 600 }}>
                3) {comment.nameFormation3}
              </Typography>
            )}
          </Box>
        )}
        
        {(comment.qualification || comment.reason) && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {comment.qualification && (
              <Chip 
                label={comment.qualification} 
                size="small" 
                sx={{ 
                  bgcolor: comment.qualification.includes('Vente') ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.1)', 
                  color: comment.qualification.includes('Vente') ? '#10b981' : 'primary.main', 
                  fontWeight: 700, 
                  fontSize: '0.65rem' 
                }} 
              />
            )}
            {comment.reason && (
              <Chip 
                label={comment.reason} 
                size="small" 
                variant="outlined"
                sx={{ fontSize: '0.65rem' }}
              />
            )}
          </Box>
        )}
      </Paper>
    </Box>
  </motion.div>
);

const QuickPreviewCard = ({ comment, title, icon: Icon, iconColor, onClick }) => (
  <motion.div whileHover={{ y: -5, cursor: 'pointer' }} onClick={onClick}>
    <Card className="glass-card" sx={{ height: '100%', border: '1px solid rgba(255,255,255,0.8)' }}>
      <CardHeader 
        title={title === 'Commercial' ? 'Notes Commerciales' : 'Dernier commentaire Relance'} 
        titleTypographyProps={{ variant: 'subtitle2', fontWeight: 900, color: '#1b2445', textTransform: 'uppercase', fontSize: '0.75rem' }}
        avatar={<Icon sx={{ color: `${iconColor}.main` }} />}
        action={
          <Tooltip title="Voir l'historique">
            <IconButton size="small"><HistoryIcon fontSize="small" /></IconButton>
          </Tooltip>
        }
      />
      <Divider sx={{ opacity: 0.5 }} />
      <CardContent sx={{ p: 3 }}>
        {comment ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ color: `${iconColor}.main`, fontWeight: 900 }}>
                  {comment.firstname} {comment.lastname}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {moment(comment.createdAt).format('DD/MM/YY')}
                </Typography>
              </Box>
              
              {/* Qualification highlight */}
              {comment.qualification && (
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: comment.qualification.includes('Vente') ? '#10b981' : 'primary.main', mb: 0.5 }}>
                  {comment.qualification}
                </Typography>
              )}

              {/* Formations list in preview */}
              {comment.nameFormation1 && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1.2 }}>
                  1) {comment.nameFormation1}
                </Typography>
              )}
              {comment.nameFormation2 && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1.2 }}>
                  2) {comment.nameFormation2}
                </Typography>
              )}

              <Typography variant="body2" sx={{ mt: 1, color: '#444', fontStyle: 'italic', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4 }}>
                "{comment.comment}"
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ py: 2, textAlign: 'center', opacity: 0.5 }}>
            <Typography variant="caption">Aucun commentaire enregistré</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  </motion.div>
);

const CustomerContactProDetails = (props) => {
  const { 
    commercialComments = [], 
    relanceComments = [], 
    callTrackRelanceList = [], 
    antecedents, 
    comments, 
    comment_relance, 
    comment_commercial, 
    editing, 
    values, 
    handleChange, 
    customerId, 
    customer,
    setCurrentTab,
    formations = [],
    user,
    ...other
  } = props;

  // Commentaire(s) issus du/des formulaire(s) de vente sales_crm, posés sur les
  // learner_session (customer.formations[].sourceComment) à l'ajout d'une formation.
  const salesFormComments = (customer?.formations || [])
    .map((f) => ({
      formationName: f?.name || '',
      comment: (f?.sourceComment ?? f?.source_comment ?? '').trim(),
    }))
    .filter((item) => item.comment !== '');

  const [drawer, setDrawer] = useState({ open: false, type: 'commercial' });
  const [etablissement, setEtablissement] = useState(null);
  const [etablissements, setEtablissements] = useState([]);
  const [etablissementsBelongsProfessional, setEtablissementsBelongsProfessional] = useState([]);
  const [loadingEtab, setLoadingEtab] = useState(true);
  const [createdEstablishmentId, setCreatedEstablishmentId] = useState([]);
  const [selectedFullText, setSelectedFullText] = useState({ open: false, title: '', content: '' });

  const handleOpenFullText = (title, content) => {
    setSelectedFullText({ open: true, title, content });
  };

  const handleCloseFullText = () => {
    setSelectedFullText({ ...selectedFullText, open: false });
  };

  const getEtablissement = useCallback(async () => {
    if (!customerId || customerId === 'undefined') return;
    try {
      const data = await etablissementApi.getCustomersEtablissementProfessionnel(customerId, localStorage.getItem('accessToken'));
      if (data) {
        setEtablissement(data.etablissementProfessional);
        setEtablissements(data.etablissements || []);
        setEtablissementsBelongsProfessional(data.etablissementsBelongsProfessional || []);
      }
      setLoadingEtab(false);
    } catch (err) {
      console.error(err);
      setLoadingEtab(false);
    }
  }, [customerId]);

  const createEtablissement = async (index) => {
    const target = etablissements[index];
    const establishment = { 
      name: `${target.firstname} ${target.lastname}`,
      email: target.email,
      adress: target.adress,
      zip: target.zip,
      city: target.city,
      department: target.department,
      region: target.region,
      phone: target.phone,
      secteur: '',
      type: '',
      website: '',
      linkedin: '',
      facebook: '',
      siret: '' 
    };
    try {
      const etablissementId = await etablissementApi.createEtablissement(establishment, localStorage.getItem('accessToken'));
      await etablissementApi.addCustomer({ 
        customerId: target.id, 
        institutionId: etablissementId.data.etablissement_id 
      }, localStorage.getItem('accessToken'));
      
      const tempCreated = [...createdEstablishmentId];
      tempCreated.push(index);
      setCreatedEstablishmentId(tempCreated);
      toast.success('Établissement créé avec succès !');
      getEtablissement(); // Rafraîchir les données
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la création de l\'établissement');
    }
  };

  useEffect(() => {
    getEtablissement();
  }, [getEtablissement]);

  const customerFormations = customer?.formations || [];
  const totalFormations = customerFormations.length;
  
  const statusCounts = customerFormations.reduce((acc, f) => {
    const status = f.status || "Autre";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const formationsByStatus = customerFormations.reduce((acc, f) => {
    const status = f.status || "Autre";
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(f);
    return acc;
  }, {});

  const getStatusColor = (status) => {
    switch (status) {
      case "Inscrit":
        return { color: "#10b981", bgcolor: "rgba(16, 185, 129, 0.1)" };
      case "En attente":
      case "À transférer":
        return { color: "#f59e0b", bgcolor: "rgba(245, 158, 11, 0.1)" };
      case "InscritD":
        return { color: "#6366f1", bgcolor: "rgba(99, 102, 241, 0.1)" };
      case "Décalé":
        return { color: "#8b5cf6", bgcolor: "rgba(139, 92, 246, 0.1)" };
      default:
        return { color: "#ef4444", bgcolor: "rgba(239, 68, 68, 0.1)" };
    }
  };

  const toggleDrawer = (type) => {
    setDrawer({ open: true, type });
  };

  return (
    <Box {...other}>
      <Grid container spacing={3}>
        {/* Formations KPI Card */}
        <Grid item xs={12}>
          <motion.div whileHover={{ y: -2 }}>
            <Card className="glass-card" sx={{ p: 0, overflow: 'hidden', position: 'relative' }}>
              <CardHeader 
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SchoolIcon sx={{ color: '#6366F1' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#1e293b' }}>
                      Indicateurs de Formations
                    </Typography>
                    <Chip 
                      label={`${totalFormations} au total`} 
                      size="small" 
                      sx={{ 
                        bgcolor: 'rgba(99, 102, 241, 0.1)', 
                        color: '#6366F1', 
                        fontWeight: 800,
                        fontSize: '0.7rem',
                        height: 20
                      }} 
                    />
                  </Box>
                }
                action={
                  setCurrentTab && (
                    <Button 
                      variant="text" 
                      size="small"
                      endIcon={<ChevronRightIcon fontSize="small" />} 
                      onClick={() => setCurrentTab("formations")}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 800, 
                        color: '#6366f1',
                        mr: 1,
                        mt: 0.5,
                        '&:hover': {
                          background: 'rgba(99, 102, 241, 0.05)',
                        }
                      }}
                    >
                      Détails
                    </Button>
                  )
                }
              />
              <Divider />
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {Object.entries(statusCounts).map(([status, count]) => {
                    const config = getStatusColor(status);
                    const displayLabel = status === 'InscritD' ? 'Inscrit D' : status;
                    const list = formationsByStatus[status] || [];
                    const studentPrenom = customer?.firstname || '';
                    const studentNom = customer?.lastname || '';

                    return (
                      <Tooltip 
                        key={status}
                        title={
                          list.length > 0 ? (
                            <Box sx={{ width: '400px', overflow: 'hidden', borderRadius: '16px' }}>
                              {/* Header du popup */}
                              <Box
                                sx={{
                                  background: 'linear-gradient(135deg, #1b5e8a, #2196f3)',
                                  p: '12px 16px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: '#ffffff',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                  }}
                                >
                                  📋 Inscriptions - {displayLabel}
                                </Typography>
                                <Box
                                  sx={{
                                    backgroundColor: 'rgba(255,255,255,0.25)',
                                    borderRadius: '12px',
                                    px: 1,
                                    py: '2px',
                                  }}
                                >
                                  <Typography sx={{ color: '#fff', fontSize: '12px', fontWeight: 700 }}>
                                    {list.length}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Liste des formations */}
                              <Box sx={{ maxHeight: '340px', overflowY: 'auto', backgroundColor: '#ffffff' }}>
                                {list.map((f, idx) => {
                                  const prog = f.progression !== undefined && f.progression !== null ? Number(f.progression) : null;
                                  return (
                                    <Box
                                      key={f.id || idx}
                                      sx={{
                                        p: '10px 16px',
                                        borderBottom: idx < list.length - 1 ? '1px solid #f0f4f8' : 'none',
                                        '&:hover': { backgroundColor: '#f0f7ff' },
                                        transition: 'background-color 0.15s ease',
                                      }}
                                    >
                                      {/* Apprenant */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', mb: '4px' }}>
                                        <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#1b2445' }}>
                                          {studentPrenom} {studentNom}
                                        </Typography>
                                        {customer?.profession && (
                                          <Typography sx={{ ml: 1, fontSize: '10px', color: '#6b7280', fontStyle: 'italic' }}>
                                            · {customer.profession}
                                          </Typography>
                                        )}
                                      </Box>

                                      {/* Formation */}
                                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: '2px' }}>
                                        <Typography sx={{ fontSize: '10px', color: '#2196f3', fontWeight: 600, mr: '4px', flexShrink: 0 }}>
                                          🎓
                                        </Typography>
                                        <Typography sx={{ fontSize: '11px', color: '#374151', lineHeight: 1.3 }}>
                                          {f.name || 'N/A'}
                                        </Typography>
                                      </Box>

                                      {/* Session */}
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: '10px', color: '#10b981', fontWeight: 600, mr: '4px' }}>
                                          📅
                                        </Typography>
                                        <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                                          Session {f.number_session || f.numberSession || '-'}
                                          {(f.start_date || f.end_date) && (
                                            <> · {f.start_date && moment(f.start_date).isValid() ? moment(f.start_date).format('DD/MM/YYYY') : ''}
                                            {f.end_date && moment(f.end_date).isValid() ? ` → ${moment(f.end_date).format('DD/MM/YYYY')}` : ''}</>
                                          )}
                                        </Typography>
                                        {prog !== null && (
                                          <Typography sx={{ ml: 'auto', fontSize: '11px', color: '#1b5e8a', fontWeight: 600 }}>
                                            {prog}%
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          ) : (
                            "Aucune formation"
                          )
                        }
                        arrow 
                        placement="top"
                        slotProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: '#ffffff !important',
                              color: '#1b2445 !important',
                              boxShadow: '0 16px 48px rgba(1,83,138,0.22), 0 2px 8px rgba(0,0,0,0.08) !important',
                              borderRadius: '16px !important',
                              border: '1px solid rgba(1,83,138,0.1) !important',
                              padding: '0 !important',
                              maxWidth: '440px !important',
                              overflow: 'hidden'
                            }
                          },
                          arrow: {
                            sx: {
                              color: '#ffffff !important'
                            }
                          }
                        }}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: '#ffffff !important',
                              color: '#1b2445 !important',
                              boxShadow: '0 16px 48px rgba(1,83,138,0.22), 0 2px 8px rgba(0,0,0,0.08) !important',
                              borderRadius: '16px !important',
                              border: '1px solid rgba(1,83,138,0.1) !important',
                              padding: '0 !important',
                              maxWidth: '440px !important',
                              overflow: 'hidden'
                            }
                          },
                          arrow: {
                            sx: {
                              color: '#ffffff !important'
                            }
                          }
                        }}
                      >
                        <Box sx={{ flex: '1 1 120px', minWidth: '100px', maxWidth: { sm: '200px' } }}>
                          <motion.div whileHover={{ scale: 1.03, y: -2 }} transition={{ duration: 0.2 }}>
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                                p: 1.8, 
                                borderRadius: '16px', 
                                border: `1px solid ${config.color}20`, 
                                bgcolor: config.bgcolor,
                                color: config.color,
                                textAlign: 'left',
                                cursor: 'pointer',
                                minHeight: '75px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02)',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  boxShadow: `0 10px 15px -3px ${config.color}15`,
                                  borderColor: config.color
                                }
                              }}
                            >
                              <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5 }}>
                                {count}
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.85 }}>
                                {displayLabel}
                              </Typography>
                            </Box>
                          </motion.div>
                        </Box>
                      </Tooltip>
                    );
                  })}
                  {totalFormations === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, width: '100%', opacity: 0.5 }}>
                      <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>Aucune formation inscrite</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Commentaire(s) du formulaire de vente sales_crm */}
        {salesFormComments.length > 0 && (
          <Grid item xs={12}>
            <Card className="glass-card" sx={{ border: '1px solid rgba(99,102,241,0.25)', bgcolor: 'rgba(99,102,241,0.04)' }}>
              <CardHeader
                title="Commentaire — Formulaire de vente"
                titleTypographyProps={{ variant: 'subtitle2', fontWeight: 900, color: '#4338ca', textTransform: 'uppercase', fontSize: '0.75rem' }}
                avatar={<CommentIcon sx={{ color: '#6366f1' }} />}
              />
              <CardContent sx={{ pt: 0 }}>
                {salesFormComments.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: index < salesFormComments.length - 1 ? 1.5 : 0,
                      p: 1.5,
                      borderRadius: '10px',
                      bgcolor: 'white',
                      border: '1px solid rgba(99,102,241,0.12)',
                    }}
                  >
                    {item.formationName && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', color: '#6366f1', fontWeight: 800, fontSize: '0.65rem', mb: 0.5 }}
                      >
                        {item.formationName}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      sx={{ color: '#334155', fontWeight: 600, whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}
                    >
                      {item.comment}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Top row: Interactive Quick Preview Cards */}
        <Grid item xs={12} md={6}>
          <QuickPreviewCard
            comment={comment_commercial}
            title="Commercial" 
            icon={AttachMoneyIcon} 
            iconColor="primary" 
            onClick={() => toggleDrawer('commercial')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <QuickPreviewCard 
            comment={comment_relance} 
            title="Relance" 
            icon={EmailIcon} 
            iconColor="success" 
            onClick={() => toggleDrawer('relance')}
          />
        </Grid>

        {/* Bottom row: Editable fields */}
        <Grid item xs={12} lg={6}>
          <motion.div whileHover={{ y: -2 }}>
            <Card className="glass-card" sx={{ height: 180, display: 'flex', flexDirection: 'column' }}>
              <CardHeader 
                title="Antécédents" 
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 900 }}
                avatar={<TimelineIcon color="secondary" />}
              />
              <Divider />
              <CardContent sx={{ flexGrow: 1, overflow: 'hidden', p: 0 }}>
                {editing ? (
                  <TextareaAutosize
                    minRows={6}
                    style={{ width: '100%', height: '100%', padding: '15px', borderRadius: '0', border: 'none', fontFamily: 'inherit', resize: 'none' }}
                    name="antecedents"
                    value={values.antecedents}
                    onChange={handleChange}
                  />
                ) : (
                  <Box 
                    onClick={() => handleOpenFullText("Antécédents", antecedents || "Aucun antécédent renseigné.")}
                    sx={{ 
                      height: '100%',
                      p: 3, 
                      cursor: 'pointer',
                      overflowY: 'auto',
                      bgcolor: 'rgba(0,0,0,0.02)',
                      transition: 'background 0.2s',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                      '&::-webkit-scrollbar': { width: '4px' },
                      '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: '4px' }
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {antecedents || "Aucun antécédent renseigné."}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} lg={6}>
          <motion.div whileHover={{ y: -2 }}>
            <Card className="glass-card" sx={{ height: 180, display: 'flex', flexDirection: 'column' }}>
              <CardHeader 
                title="Observations" 
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 900 }}
                avatar={<ErrorOutlineIcon sx={{ color: '#f59e0b' }} />}
              />
              <Divider />
              <CardContent sx={{ flexGrow: 1, overflow: 'hidden', p: 0 }}>
                {editing ? (
                  <TextareaAutosize
                    minRows={6}
                    style={{ width: '100%', height: '100%', padding: '15px', borderRadius: '0', border: 'none', fontFamily: 'inherit', resize: 'none' }}
                    name="comments"
                    value={values.comments}
                    onChange={handleChange}
                  />
                ) : (
                  <Box 
                    onClick={() => handleOpenFullText("Observations", comments || "Aucune observation particulière.")}
                    sx={{ 
                      height: '100%',
                      p: 3, 
                      cursor: 'pointer',
                      overflowY: 'auto',
                      bgcolor: 'rgba(245, 158, 11, 0.02)',
                      transition: 'background 0.2s',
                      '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.04)' },
                      '&::-webkit-scrollbar': { width: '4px' },
                      '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: '4px' }
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {comments || "Aucune observation particulière."}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12}>
          <motion.div whileHover={{ y: -2 }}>
            <Card className="glass-card" sx={{ p: 0, overflow: 'hidden', position: 'relative' }}>
              <CardHeader 
                title="Établissement" 
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 900 }}
                avatar={<BusinessIcon sx={{ color: 'primary.main' }} />}
              />
              <Divider />
              <CardContent>
                {loadingEtab ? (
                  <Typography variant="body2" color="textSecondary">Chargement...</Typography>
                ) : (
                  <Box>
                    {/* current establishment */}
                    {etablissement && etablissement.idEtablissements ? (
                      <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(99, 102, 241, 0.05)', borderRadius: '12px', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                        <Typography variant="caption" sx={{ fontWeight: 900, color: 'primary.main', textTransform: 'uppercase' }}>Établissement actuel</Typography>
                        <Typography 
                          component={RouterLink}
                          to={`/dashboard/etablissements/${etablissement.idEtablissements}`}
                          variant="subtitle1" 
                          sx={{ 
                            display: 'block',
                            color: '#1e293b', 
                            textDecoration: 'none',
                            fontWeight: 800,
                            mt: 0.5,
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {etablissement.etablissementsName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                          {etablissement.etablissementsAdress} {etablissement.city}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                        Cette personne n'appartient à aucun établissement.
                      </Typography>
                    )}

                    {/* Identicals list */}
                    {etablissements.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 4, height: 16, bgcolor: 'warning.main', borderRadius: 1 }} />
                          Numéros et adresses identiques
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {etablissements.map((link, index) => (
                            <Box key={link.id} sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                  <Avatar 
                                    src={link.avatar} 
                                    sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: 'primary.light' }}
                                  >
                                    {link.firstname?.[0]}{link.lastname?.[0]}
                                  </Avatar>
                                  <Box>
                                    <Typography 
                                      component={RouterLink}
                                      to={`/dashboard/customers/${link.id}`}
                                      variant="subtitle2" 
                                      sx={{ fontWeight: 800, color: link.treated === 1 ? '#10b981' : '#1e293b', textDecoration: 'none' }}
                                    >
                                      {link.firstname} {link.lastname}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                      {link.profession}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>

                              <Typography variant="caption" sx={{ display: 'block', color: '#475569', mb: 0.5 }}>
                                📍 {link.adress}, {link.city}
                              </Typography>
                              {link.phone && (
                                <Typography variant="caption" sx={{ display: 'block', color: '#475569', fontWeight: 600 }}>
                                  📞 {link.phone}
                                </Typography>
                              )}

                              <Box sx={{ mt: 2 }}>
                                {link.etablissementsName !== null ? (
                                  <Chip 
                                    label={link.etablissementsName} 
                                    size="small" 
                                    sx={{ bgcolor: 'rgba(99, 102, 241, 0.1)', color: 'primary.main', fontWeight: 700, fontSize: '0.65rem' }} 
                                  />
                                ) : (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    startIcon={<BusinessIcon fontSize="small" />}
                                    fullWidth
                                    onClick={() => createEtablissement(index)}
                                    disabled={createdEstablishmentId.includes(index)}
                                    sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '0.7rem', py: 0.5 }}
                                  >
                                    Créer un établissement
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Colleagues list */}
                    {etablissementsBelongsProfessional.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 4, height: 16, bgcolor: 'primary.main', borderRadius: 1 }} />
                          Collègues ({etablissementsBelongsProfessional.length})
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {etablissementsBelongsProfessional.map((link) => (
                            <Box 
                              key={link.id} 
                              component={RouterLink}
                              to={`/dashboard/customers/${link.id}`}
                              sx={{ 
                                p: 1.5, 
                                borderRadius: '10px', 
                                border: '1px solid #f1f5f9', 
                                bgcolor: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                textDecoration: 'none',
                                transition: 'all 0.2s',
                                '&:hover': { bgcolor: '#f8fafc', borderColor: '#e2e8f0' }
                              }}
                            >
                              <Avatar 
                                src={link.avatar} 
                                sx={{ width: 28, height: 28, fontSize: '0.65rem', bgcolor: 'success.light' }}
                              >
                                {link.firstname?.[0]}{link.lastname?.[0]}
                              </Avatar>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: link.treated === 1 ? '#10b981' : '#1e293b', display: 'block' }}>
                                  {link.firstname} {link.lastname}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
                                  {link.profession}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        <CustomerRelationScore customerId={customerId} />
      </Grid>

      {/* Side History Drawer */}
      <Drawer
        anchor="right"
        open={drawer.open}
        onClose={() => setDrawer({ ...drawer, open: false })}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 450 }, bgcolor: '#f1f5f9', p: 0 }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 3, bgcolor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ p: 1, borderRadius: '10px', bgcolor: drawer.type === 'commercial' ? 'primary.main' : 'success.main', color: 'white', display: 'flex' }}>
                {drawer.type === 'commercial' ? <AttachMoneyIcon /> : <EmailIcon />}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 900, color: '#1e293b' }}>
                Historique {drawer.type === 'commercial' ? 'Commercial' : 'Relances'}
              </Typography>
            </Box>
            <IconButton onClick={() => setDrawer({ ...drawer, open: false })}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flexGrow: 1, p: 3, overflowY: 'auto', bgcolor: 'white' }}>
            {drawer.type === 'commercial' ? (
              <CustomerCommercialActions
                customerId={customerId}
                user={user}
                customer={customer}
                formations={formations}
              />
            ) : (
              <CustomerRelance
                customerId={customerId}
                user={user}
              />
            )}
          </Box>
          
          <Box sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid #e2e8f0' }}>
            <Button 
              fullWidth 
              variant="contained" 
              onClick={() => setDrawer({ ...drawer, open: false })}
              sx={{ borderRadius: '12px', py: 1.5, fontWeight: 800 }}
            >
              Fermer
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Full Text Dialog */}
      <Dialog 
        open={selectedFullText.open} 
        onClose={handleCloseFullText}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '20px', p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: '#1e293b' }}>
          {selectedFullText.title}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, color: '#475569' }}>
            {selectedFullText.content}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseFullText} variant="contained" sx={{ borderRadius: '10px', px: 4 }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

CustomerContactProDetails.propTypes = {
  commercialComments: PropTypes.array,
  callTrackRelanceList: PropTypes.array,
  antecedents: PropTypes.string,
  comments: PropTypes.string,
  comment_relance: PropTypes.object,
  comment_commercial: PropTypes.object,
  editing: PropTypes.bool,
  values: PropTypes.object,
  handleChange: PropTypes.func
};

export default CustomerContactProDetails;
